import { Endpoint } from "../Configuration";
import {
  JobSpecificationOverview,
  JobSpecificationResponse,
  JobSpecification,
} from "./JobSpecification";
import AdviseeValidationResult from "./AdviseeValidationResult";
import { UCXCode, UCXResponse } from "./UCX";
import {
  JobInstanceForPosting,
  JobInstanceListResponse,
  JobInstance,
  FileInputParameter,
} from "./JobInstance";
import { SortDirection } from "@ellucian/react-design-system/core/es/Table";
import FormData from 'form-data';
import { Schedule } from "../Scheduler";
import { ScheduledRequestListResponse } from "../ScheduledRequests/Properties/ScheduledRequestListResponse"
import { SaveJob, SaveJobList } from "../SavedJob/Properties/SaveJob";
import { addParameter } from "../Utils/Utilities";
import { Sequence, SequenceList } from "../Sequence/Properties/Sequence";

const instanceType = 'application/vnd.hedtech.degreeworks.transit.instance.v2.hal+json';
const scheduleType = 'application/vnd.hedtech.degreeworks.transit.schedule.v1.hal+json';
const saveJobType = 'application/vnd.hedtech.degreeworks.transit.savedJob.v1.hal+json';
const sequenceJobType = 'application/vnd.hedtech.degreeworks.transit.sequence.v1.hal+json';
const plainType = "text/plain";
const jsonType = "application/json";
const appId = "TRANSITUI";

export enum Url {
  SAVE_JOB = '/api/savedJob',
  SCHEDULE_JOB = '/api/scheduledRequest',
  SEQUENCE = '/api/sequence',
  SETTING_BY_PREFIX = 'api/settings/prefix',
  MY_INFO = 'api/users/myself',
}

export function getEndpoint(url: string): string {
  let ep = Endpoint();
  if (!ep.endsWith("/")) ep += "/";
  if (url.startsWith("/")) url = url.substr(1);
  return `${ep}${url}`;
}

export interface ErrorResponse {
  errorText: string;
  errorCode: number;
}

export function isErrorResponse(item: any): item is ErrorResponse {
  if (!item) return false;
  if ((<ErrorResponse>item).errorText || (<ErrorResponse>item).errorCode)
    return true;
  return false;
}

/**
 * Gets endpoint data. Returns "ErrorResponse" if an error is received.
 * 
 * @param url - endpoint of an API,  example - /api/instance
 * @param acceptType - accept type of response body content.
 * @returns Promise return from the Rest API. (Successfull or error body)
 */
export async function getSafe<T>(url: string, acceptType: string | undefined): Promise<T | ErrorResponse> {
  const endpoint = getEndpoint(url);
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      acceptType: acceptType || '',
    },
  });

  if (!response.ok) {
    if (response.status == 401 || response.status == 403) {
      window.open(".", "_self");
    } else {
      return await getError(response);
    }
  }
  return <T>(<any>response.json());
}

/**
 * Gets endpoint data. Throws exception if an error is received.
 * 
 * @param url - endpoint of an API,  example - /api/instance
 * @param acceptType - accept type of response body content. It is an optional param.
 * @returns Promise return from the Rest API. (Successfull or error body)
 */
export async function get<T>(url: string, acceptType?: string): Promise<T> {
  const response = await getSafe<T>(url, acceptType);

  if (isErrorResponse(response)) {
    throw response;
  }
  return response;
}

/**
 * Call a Rest API type of POST, and PUT.
 * @param url - endpoint of an API,  example - /api/instance
 * @param data - Request body content.
 * @param contentType - Type of response body content.
 * @param acceptType - Type of request body content.
 * @param isPUT  - Is the Http method is PUT.
 * @param ifMatch - If-match header to optimistic locking is used in the new APIs to guarantee data consistency. Example 3db45b9e5adf4ba0b55f16dacf34a14f-9.
 * @returns Promise return from the Rest API. (Successfull or error body)
 */
export async function save<T, R>(
  url: string,
  data: T,
  contentType: string,
  acceptType: string,
  isPUT: boolean = false,
  ifMatch: string = '',
): Promise<R> {
  const endpoint = getEndpoint(url);
  const response = await fetch(endpoint, {
    method: isPUT ? "PUT" : "POST",
    cache: "no-cache",
    headers: {
      "Content-Type": contentType,
      Accept: acceptType,
      HEDTECH_DEGREEWORKS_APPID: appId,
      'if-match': ifMatch,
    },
    referrer: "no-referrer",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status == 401 || response.status == 403) {
      window.open(".", "_self");
    } else {
      throw await getError(response);
    }
  }
  return acceptType === plainType
    ? <R>(<any>await response.text())
    : <R>(<any>response.json());
}

/**
 * Call a Rest API to delete a resource.
 * 
 * @param url - endpoint of an API,  example - /api/instance/123
 * @returns Promise return from the Rest API. (Successfull or error body) or Null
 */
export async function del<T>(url: string, ifMatch: string = ''): Promise<T | ErrorResponse | null> {
  const endpoint = getEndpoint(url);
  const response = await fetch(endpoint, {
    method: "DELETE",
    headers: {
      HEDTECH_DEGREEWORKS_APPID: appId,
      'if-match': ifMatch,
    },
  });

  if (!response.ok) {
    if (response.status == 401 || response.status == 403) {
      window.open(".", "_self");
    } else {
      return await getError(response);
    }
  }

  if (response.bodyUsed) {
    return <T>(<any>response.json());
  }

  return null;
}

async function getError(response: Response) {
  try {
    const error = await response.json();
    try {
      const message = error.errors[0].details[0].message;
      return {
        errorText: message,
        errorCode: response.status,
      };
    } catch {
      // fall back to status text.
    }

    return {
      errorText: response.statusText,
      errorCode: response.status,
    };
  }
  catch (error) {
    return {
      errorText: undefined,
      errorCode: response.status,
    };
  }
}

// Cache for storing job specifications retrieved from an API, stores till Browser session.
const JobSpecificationsCache: { [specifications: string]: JobSpecificationOverview[] } = {};

// Retrieves job specifications from an API, utilizing caching to avoid redundant API calls.
export async function getJobSpecifications(): Promise<
  JobSpecificationOverview[]
> {
  // Check if the specifications are already in the cache
  const cachedResponse = Object.values(JobSpecificationsCache)[0];
  if (cachedResponse) {
    return Promise.resolve(cachedResponse);
  }
  // If specifications are not in the cache, make an API call
  const specifications = (await get<JobSpecificationResponse>("api/transit/specifications"))
    .specifications;
  JobSpecificationsCache['specifications'] = specifications;
  return specifications;
}

// Cache for storing job specification retrieved from an API, stores till Browser session.
const JobSpecificationCache: { [specification: string]: JobSpecification } = {};

// Retrieves job specification from an API, utilizing caching to avoid redundant API calls.
export async function getJobSpecification(
  jobName: string
): Promise<JobSpecification> {
  // Check if the specification is already in the cache
  const cachedResponse = JobSpecificationCache[jobName];
  if (cachedResponse) {
    return Promise.resolve(cachedResponse);
  }
  // If specification is not in the cache, make an API call
  const specification = await get<JobSpecification>(jobName ? `api/transit/specifications/${jobName}` : `api/transit/specifications`);
  JobSpecificationCache[jobName] = specification;
  return specification;
}

export async function validateAdvisee(
  adviseeId: string
): Promise<AdviseeValidationResult | ErrorResponse> {
  return await getSafe<AdviseeValidationResult>(`api/students/${adviseeId}`, undefined);
}

const UCXCache: { [table: string]: UCXResponse } = {};

// The 'filterFlag' is a flag to filter on the table, and 'filterValues' are the values to include, and the remaining to be filtered out.
export async function getUCXCodes(table: string, filterFlag: string = '',
  filterValues: string = ''): Promise<UCXResponse> {
  // simple cache optimization. Remove if causing issues...
  // If filterFlag and filterValues do not exist, get values from the cache
  if (!filterFlag && !filterValues) {
    const cached = UCXCache[table];
    if (cached) {
      return Promise.resolve(cached);
    }
  }
  let url = `api/transit/validations/${table}`;
  url += `?filterFlag=${filterFlag}&filterValues=${encodeURIComponent(filterValues)}`;

  const response = await get<UCXResponse>(url);
  // Set descending order for the tables
  if (table === "STU035" || table === "STU016") {
    const sortedArray: UCXCode[] = response.validationCodes.sort((o1, o2) => {
      if (o1.key > o2.key) {
        return -1;
      }
      if (o1.key < o2.key) {
        return 1;
      }
      return 0;
    });
    response.validationCodes = sortedArray;
    UCXCache[table] = response;
  }
  // If filterFlag and filterValues do not exist, set values to the cache
  if (!filterFlag && !filterValues) {
    UCXCache[table] = response;
  }

  return response;
}

export async function launchJobInstance(
  job: JobInstanceForPosting
): Promise<JobInstance> {
  return await save<JobInstanceForPosting, JobInstance>(
    "api/transit/instances",
    job,
    instanceType,
    instanceType
  );
}

/**
 * Get a schedule job.
 * 
 * @param scheduledId - a scheduled job Id.
 * @returns a schedule job data.
 */
export async function getScheduleJob(
  scheduledId: string
): Promise<Schedule> {
  const url = `${Url.SCHEDULE_JOB}/${encodeURIComponent(scheduledId)}`;
  return await get<Schedule>(url, scheduleType);
}

/**
 * Create a schedule.
 * 
 * @param schedule - schedule job information.
 * @returns Promise of schedule request.
 */
export async function scheduleJob(
  schedule: Schedule
): Promise<Schedule> {
  return await save<Schedule, Schedule>(
    Url.SCHEDULE_JOB,
    schedule,
    scheduleType,
    scheduleType
  );
}

/**
 * Update a scheduled job.
 * 
 * @param scheduledId  - A schedule job id.
 * @param schedule - schedule job information.
 * @param lockVersion - lock version of a update schedule request.
 * @returns Promise of schedule request.
 */
export async function updateScheduleJob(
  scheduledId: string,
  schedule: Schedule,
  lockVersion: string,
): Promise<Schedule> {
  const ifMatch = `${scheduledId}-${lockVersion}`;
  const url = `${Url.SCHEDULE_JOB}/${encodeURIComponent(scheduledId)}`
  return await save<Schedule, Schedule>(
    url,
    schedule,
    scheduleType,
    scheduleType,
    true,
    ifMatch,
  );
}

/**
 * Delete a given scheduled request by ID.
 * @param scheduleId 
 * @param lockVersion 
 * @returns 
 */
export async function deleteScheduledRequest(scheduleId: string, lockVersion?: string): Promise<void> {
  const url = `${Url.SCHEDULE_JOB}/${encodeURIComponent(scheduleId)}`
  const ifMatch = `${scheduleId}-${lockVersion}`;
  const result = await del<void>(url, ifMatch);
  if (isErrorResponse(result)) throw result;
  return;
}

/**
 * Deletes the next run of a scheduled request. 
 * This doesn't actually delete anything, it simply recalculates the next scheduled time based on the current scheduled time.
 * 
 * @param scheduleId 
 * @param lockVersion 
 * @returns 
 */
export async function deleteNextScheduledRequest(scheduleId: string, lockVersion?: string) {
  const url = `${Url.SCHEDULE_JOB}/${encodeURIComponent(scheduleId)}/next`
  const ifMatch = `${scheduleId}-${lockVersion}`;
  const result = await del<void>(url, ifMatch);
  if (isErrorResponse(result)) throw result;
  return;
}

/**
 * Delete a given saved job by ID.
 * @param savedJobId 
 * @param lockVersion 
 * @returns 
 */
export async function deleteSavedJob(savedJobId: string, lockVersion?: string): Promise<void> {
  const URL = `${Url.SAVE_JOB}/${encodeURIComponent(savedJobId)}`
  const ifMatch = `${savedJobId}-${lockVersion}`;
  const result = await del<void>(URL, ifMatch);
  if (isErrorResponse(result)) throw result;
  return;
}

// Makes an API call to create the uploaded zip file into Transfer_Input table and return the uuid of the created entity.
export async function saveJobFileInput(
  fileInput: FileInputParameter
): Promise<any> {

  const form = new FormData();
  form.append('filename', fileInput.filename);
  form.append('questionId', fileInput.questionId);
  form.append('payload', new Blob([fileInput.input], { type: "application/octet-stream" }));

  const endpoint = getEndpoint(`api/transit/inputs`);
  const response = await fetch(endpoint, {
    method: "POST",
    cache: "no-cache",
    headers: {
      Accept: `${plainType}, ${jsonType}`,
      HEDTECH_DEGREEWORKS_APPID: appId,
    },
    referrer: "no-referrer",
    body: form as any,
  });
  if (!response.ok) {
    if (response.status == 401 || response.status == 403) {
      window.open(".", "_self");
    } else {
      throw await getError(response);
    }
  }
  return await response.text();
}

export async function getStudentCount(
  job: JobInstanceForPosting
): Promise<any> {
  return await save<JobInstanceForPosting, any>(
    "api/transit/student-count",
    job,
    instanceType,
    `${plainType}, ${jsonType}`
  );
}

export async function getInstances(
  filter?: string,
  page?: number,
  pageSize?: number,
  sortColumn?: string,
  sortDirection?: SortDirection
): Promise<JobInstanceListResponse> {
  const queryParts = [];
  if (filter !== undefined) queryParts.push(`filter=${encodeURI(filter)}`);
  if (page !== undefined) queryParts.push(`page=${page}`);
  if (pageSize !== undefined) queryParts.push(`size=${pageSize}`);
  if (sortColumn !== undefined && sortDirection !== undefined) {
    let sort = `sort=${sortColumn}`;
    if (sortDirection == "desc") sort += ",desc";
    queryParts.push(sort);
  }

  const query = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  return await get<JobInstanceListResponse>(`api/transit/instances${query}`);
}

/**
 * Gets a list of Scheduled Job Requests
 * @param filter 
 * @param page 
 * @param pageSize 
 * @param sortColumn 
 * @param sortDirection
 * @param savedJobId
 * @returns a promise of the list.
 */
export async function getScheduledRequests(
  filter?: string,
  page?: number,
  pageSize?: number,
  sortColumn?: string,
  sortDirection?: SortDirection,
  savedJobId?: string,
  sequenceId?: string
): Promise<ScheduledRequestListResponse> {

  let url = `${Url.SCHEDULE_JOB}`;
  if (filter) {
    url = addParameter(url, 'filter', filter);
  } else {
    url = `${url}?filter=`
  }
  if (sequenceId) {
    url = addParameter(url, 'sequenceId', sequenceId);
  }
  if (savedJobId) {
    url = addParameter(url, 'savedJobId', savedJobId);
  }
  if (page) {
    url = addParameter(url, 'page', `${page}`);
  }
  if (pageSize) {
    url = addParameter(url, 'size', `${pageSize}`);
  }
  if (sortColumn && sortDirection) {
    url = addParameter(url, 'sort', `${sortColumn},${sortDirection}`);
  }

  return await get<ScheduledRequestListResponse>(url);
}

export async function deleteInstance(jobId: string): Promise<void> {
  const result = await del<void>(`api/transit/instances/${jobId}`);
  if (isErrorResponse(result)) throw result;
  return;
}

// Get the artifact as plain text
export async function getArtifactText(
  jobId: string,
  artifactId: string
): Promise<string | ErrorResponse> {
  const endpoint = getEndpoint(
    `api/transit/instances/${jobId}/artifactText/${artifactId}`
  );
  const response = await fetch(endpoint, {
    method: "GET",
  });

  if (!response.ok) {
    if (response.status == 401 || response.status == 403) {
      window.open(".", "_self");
    } else {
      return await getError(response);
    }
  }
  // response.text() - The response is always decoded using UTF-8.
  return response.text();
}

// Get the artifact as a blob for downloading
export async function getArtifactBlob(
  jobId: string,
  artifactId: string
): Promise<string | ErrorResponse> {
  const endpoint = getEndpoint(
    `api/transit/instances/${jobId}/artifacts/${artifactId}`
  );
  const response = await fetch(endpoint, {
    method: "GET",
  });

  if (!response.ok) {
    if (response.status == 401 || response.status == 403) {
      window.open(".", "_self");
    } else {
      return await getError(response);
    }
  }
  // response.text() - The response is always decoded using UTF-8.
  return response.text();
}

export async function getMessages(): Promise<any> {
  return await get<any>(`api/messages`);
}

export async function getFeatures(): Promise<any> {
  return await get<any>("api/features");
}

/** NOTE: This function gets one setting at a time. Reminder to refactor it if we decide to get additional settings from API.
* Function to get shp setting value by key
* @paam {string} a valid shp setting key
* @return {boolean}  shp setting key value
*/
export async function getSettingValueByKey(key: any): Promise<any> {
  const response: any = await get(`${Url.SETTING_BY_PREFIX}/${encodeURIComponent(key)}`);
  return response[0]?.value ?? null;
}

// Saved Jobs

/**
 * Gets a list of Saved Jobs
 * @param filter 
 * @param page 
 * @param pageSize 
 * @param sortColumn 
 * @param sortDirection 
 * @returns a promise of the saved job list.
 */
export async function getSavedJobs(
  filter?: string,
  page?: number,
  pageSize?: number,
  sortColumn?: string,
  sortDirection?: SortDirection
): Promise<SaveJobList> {

  let url = addParameter(Url.SAVE_JOB, 'filter', filter);
  if (page) {
    url = addParameter(url, 'page', `${page}`);
  }
  if (pageSize) {
    url = addParameter(url, 'size', `${pageSize}`);
  }
  if (sortColumn && sortDirection) {
    url = addParameter(url, 'sort', `${sortColumn},${sortDirection}`);
  }
  return await get<SaveJobList>(url);
}

/**
 * Get a saved job by calling get a saved job.
 *
 * @param savedId - a saved job Id.
 * @returns a saved job data.
 */
export async function getSavedJob(
  savedId: string
): Promise<SaveJob> {
  const url = `${Url.SAVE_JOB}/${encodeURIComponent(savedId)}`;
  return await get<SaveJob>(url, scheduleType);
}

/**
 * Create a Saved Job record.
 * @param saveJob 
 * @returns 
 */
export async function createSavedJob(
  saveJob: SaveJob
): Promise<SaveJob> {
  return await save<SaveJob, SaveJob>(
    Url.SAVE_JOB,
    saveJob,
    saveJobType,
    saveJobType
  );
}

/**
 * Update a Saved Job record.
 * @param saveJob
 * @param lockVersion
 * @returns 
 */
export async function updateSavedJob(
  saveJob: SaveJob,
  lockVersion: string,
): Promise<SaveJob> {
  const ifMatch = `${saveJob.savedJobId}-${lockVersion}`;
  const url = `${Url.SAVE_JOB}/${encodeURIComponent(saveJob.savedJobId!)}`
  return await save<SaveJob, SaveJob>(
    url,
    saveJob,
    saveJobType,
    saveJobType,
    true,
    ifMatch,
  );
}

// Sequences
/**
 * Gets a list of Sequences
 * @param filter
 * @param page
 * @param pageSize
 * @param sortColumn
 * @param sortDirection
 * @param savedJobId
 * @returns a promise of the sequence list.
 */
export async function getSequences(
  filter?: string,
  page?: number,
  pageSize?: number,
  sortColumn?: string,
  sortDirection?: SortDirection,
  savedJobId?: string
): Promise<SequenceList> {

  let url = `${Url.SEQUENCE}`;
  if (filter) {
    url = addParameter(url, 'filter', filter);
  } else {
    url = `${url}?filter=`
  }
  if (savedJobId) {
    url = addParameter(url, 'savedJobId', savedJobId);
  }
  if (page) {
    url = addParameter(url, 'page', `${page}`);
  }
  if (pageSize) {
    url = addParameter(url, 'size', `${pageSize}`);
  }
  if (sortColumn && sortDirection) {
    url = addParameter(url, 'sort', `${sortColumn},${sortDirection}`);
  }
  return await get<SequenceList>(url);
}

/**
 * Create a sequence.
 * 
 * @param sequence 
 * @returns Promise result.
 */
export async function createSequence(
  sequence: Sequence
): Promise<Sequence> {
  return await save<Sequence, Sequence>(
    Url.SEQUENCE,
    sequence,
    sequenceJobType,
    sequenceJobType
  );
}

/**
 * Launch a sequence by sequence id.
 *
 * @param sequenceId - a unique id of a sequence to be launched.
 * @returns Promise result.
 */
export async function launchSequence(sequenceId: string) {
  const jobInstance: JobInstanceForPosting = {
    sequenceId: sequenceId,
  };
  return await launchJobInstance(jobInstance);
}

/**
 * Deletes given sequence by ID.
 * @param sequenceId 
 * @param lockVersion 
 * @returns 
 */
export async function deleteSequence(sequenceId: string, lockVersion?: string): Promise<void> {
  const URL = `${Url.SEQUENCE}/${encodeURIComponent(sequenceId)}`
  const ifMatch = `${sequenceId}-${lockVersion}`;
  const result = await del<void>(URL, ifMatch);
  if (isErrorResponse(result)) throw result;
  return;
}

/** 
* Get a sequence by calling get a sequence.
*
* @param sequenceId - a sequence Id.
* @returns a sequence.
*/
export async function getSequence(
  sequenceId: string
): Promise<Sequence> {
  const url = `${Url.SEQUENCE}/${encodeURIComponent(sequenceId)}`;
  return await get<Sequence>(url, scheduleType);
}

/**
 * Update a sequence record.
 * @param sequence
 * @param lockVersion
 * @returns 
 */
export async function updateSequence(
  sequence: Sequence,
  lockVersion: string,
): Promise<Sequence> {
  const ifMatch = `${sequence.sequenceId}-${lockVersion}`;
  const url = `${Url.SEQUENCE}/${encodeURIComponent(sequence.sequenceId!)}`
  return await save<Sequence, Sequence>(
    url,
    sequence,
    sequenceJobType,
    sequenceJobType,
    true,
    ifMatch,
  );
}
